import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases'
import { Stepper, Step } from '@/components/wizard/Stepper'
import { Step1BasicInfo } from './wizard-steps/Step1BasicInfo'
import { Step2SelectKBs } from './wizard-steps/Step2SelectKBs'
import { Step3LLMConfig, LLMConfig, DEFAULT_LLM_CONFIG } from './wizard-steps/Step3LLMConfig'
import { Step4PromptConfig, PromptConfig, DEFAULT_PROMPT_CONFIG } from './wizard-steps/Step4PromptConfig'
import { Step5Review } from './wizard-steps/Step5Review'
import knowledgeApi from '@/lib/knowledge-api'

const STEPS: Step[] = [
  { id: 1, name: 'Basic Info' },
  { id: 2, name: 'Knowledge Bases' },
  { id: 3, name: 'LLM Config' },
  { id: 4, name: 'Prompts' },
  { id: 5, name: 'Review' },
]

interface WizardData {
  name: string
  description: string
  selectedKBIds: string[]
  llmConfig: LLMConfig
  promptConfig: PromptConfig
}

interface ValidationErrors {
  name?: string
  knowledgeBases?: string
  systemPrompt?: string
}

const initialWizardData: WizardData = {
  name: '',
  description: '',
  selectedKBIds: [],
  llmConfig: DEFAULT_LLM_CONFIG,
  promptConfig: DEFAULT_PROMPT_CONFIG,
}

export function CreateAssistantWizard() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: kbData } = useKnowledgeBases({ page: 1, pageSize: 100, search: '' })
  const knowledgeBases = kbData?.items

  const [currentStep, setCurrentStep] = useState(1)
  const [wizardData, setWizardData] = useState<WizardData>(initialWizardData)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  // Get selected KB objects for review
  const selectedKBs = useMemo(() => {
    if (!knowledgeBases) return []
    return knowledgeBases.filter((kb: any) => wizardData.selectedKBIds.includes(kb.id))
  }, [knowledgeBases, wizardData.selectedKBIds])

  const validateStep = (step: number): boolean => {
    const newErrors: ValidationErrors = {}

    switch (step) {
      case 1:
        if (!wizardData.name.trim()) {
          newErrors.name = 'Name is required'
        }
        break
      case 2:
        if (wizardData.selectedKBIds.length === 0) {
          newErrors.knowledgeBases = 'Select at least one knowledge base'
        }
        break
      case 4:
        if (!wizardData.promptConfig.systemPrompt.trim()) {
          newErrors.systemPrompt = 'System prompt is required'
        }
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5))
    }
  }

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleStepClick = (step: number) => {
    // Only allow going back to completed steps
    if (step < currentStep) {
      setCurrentStep(step)
    }
  }

  const handleCancel = () => {
    // Check if any data has been entered
    const hasData =
      wizardData.name.trim() ||
      wizardData.description.trim() ||
      wizardData.selectedKBIds.length > 0

    if (hasData) {
      setShowCancelDialog(true)
    } else {
      navigate('/app/assistants')
    }
  }

  const handleConfirmCancel = () => {
    setShowCancelDialog(false)
    navigate('/app/assistants')
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return

    setIsSubmitting(true)

    try {
      const payload = {
        name: wizardData.name.trim(),
        description: wizardData.description.trim(),
        knowledge_base_ids: wizardData.selectedKBIds,
        // LLM Config
        temperature: wizardData.llmConfig.temperature,
        top_p: wizardData.llmConfig.topP,
        presence_penalty: wizardData.llmConfig.presencePenalty,
        frequency_penalty: wizardData.llmConfig.frequencyPenalty,
        // Prompt Config
        system_prompt: wizardData.promptConfig.systemPrompt,
        opener: wizardData.promptConfig.opener,
        empty_response: wizardData.promptConfig.emptyResponse,
        similarity_threshold: wizardData.promptConfig.similarityThreshold,
        keywords_similarity_weight: wizardData.promptConfig.keywordsSimilarityWeight,
        top_n: wizardData.promptConfig.topN,
        show_quote: wizardData.promptConfig.showQuote,
      }

      const response = await knowledgeApi.post('/knowledge/api/assistants/create/', payload)

      if (response.data.success) {
        toast({
          title: 'Assistant created',
          description: `"${wizardData.name}" has been created successfully.`,
        })
        // Use shared_id for URL if available, fallback to id
        const urlId = response.data.assistant.shared_id || response.data.assistant.id
        navigate(`/app/assistants/${urlId}`)
      } else {
        throw new Error(response.data.error || 'Failed to create assistant')
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create assistant'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            name={wizardData.name}
            description={wizardData.description}
            onNameChange={(name) => {
              setWizardData((prev) => ({ ...prev, name }))
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
            }}
            onDescriptionChange={(description) =>
              setWizardData((prev) => ({ ...prev, description }))
            }
            errors={errors}
          />
        )
      case 2:
        return (
          <Step2SelectKBs
            selectedKBIds={wizardData.selectedKBIds}
            onSelectionChange={(selectedKBIds) => {
              setWizardData((prev) => ({ ...prev, selectedKBIds }))
              if (errors.knowledgeBases) setErrors((prev) => ({ ...prev, knowledgeBases: undefined }))
            }}
            errors={errors}
          />
        )
      case 3:
        return (
          <Step3LLMConfig
            config={wizardData.llmConfig}
            onConfigChange={(llmConfig) =>
              setWizardData((prev) => ({ ...prev, llmConfig }))
            }
          />
        )
      case 4:
        return (
          <Step4PromptConfig
            config={wizardData.promptConfig}
            onConfigChange={(promptConfig) => {
              setWizardData((prev) => ({ ...prev, promptConfig }))
              if (errors.systemPrompt) setErrors((prev) => ({ ...prev, systemPrompt: undefined }))
            }}
            errors={errors}
          />
        )
      case 5:
        return (
          <Step5Review
            name={wizardData.name}
            description={wizardData.description}
            selectedKBs={selectedKBs}
            llmConfig={wizardData.llmConfig}
            promptConfig={wizardData.promptConfig}
          />
        )
      default:
        return null
    }
  }

  const stepName = STEPS.find((s) => s.id === currentStep)?.name || ''

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Create Assistant</h1>
        <p className="text-muted-foreground mt-1">
          Step {currentStep} of 5: {stepName}
        </p>
      </div>

      {/* Stepper */}
      <div className="mb-8">
        <Stepper
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step Content */}
      <div className="min-h-[400px] mb-8">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between border-t pt-6">
        <Button variant="ghost" onClick={handleCancel}>
          Cancel
        </Button>

        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}

          {currentStep < 5 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Assistant'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Yes, discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
